import React, { useEffect } from "react";
import { nanoid } from "nanoid";
import {
  ButtonOption,
  isWhenUserResponse,
  PersonalizationValue,
  StepConfigSendSurvey,
  TriggerStepCreate,
  TriggerStepSendSurvey,
} from "@dots-projects/types";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { CloseCircleFilled, PlusOutlined } from "@ant-design/icons";
import { Checkbox } from "antd";
import produce from "immer";
import invariant from "ts-invariant";

import { Input } from "./Input";
import { StepFormWithCommonParts } from "./TriggerStepForm";
import { FormLabel } from "./FormLabel";
import EmojiPicker from "./EmojiPicker";

import { useTriggerEditorStateDispatch } from "./TriggerEditor";
import { saveDraft, updateStep } from "./reducer/actionCreators";
import { SendStepFormWithCommonParts } from "./SendStepFormWithCommonParts";
import clsx from "clsx";

const DEFAULT_SKIP_KEYWORD = "skip";

/**
 * 25 options are the maximum number of options that Discord allows
 * but we are using 24 because the done button takes one slot in the case of
 * send_multiple_select
 * Info: https://discordjs.guide/interactions/buttons.html#building-and-sending-buttons
 *
 */
const MAX_SURVEY_OPTIONS = 24;

export function StepFormSendSurvey({
  step,
  insertVariables,
}: {
  step: TriggerStepCreate;
  insertVariables: PersonalizationValue[];
}) {
  invariant(
    step.type === "send_survey" || step.type === "send_multiple_select"
  );

  const skipId = `${DEFAULT_SKIP_KEYWORD}-${step.id}`;
  //There is an issue in ts for that reason I cast to any[] check: https://github.com/microsoft/TypeScript/issues/44373
  const skipOption = (step.configs.options as any[]).find(
    (o: ButtonOption) => (o as ButtonOption).id === skipId
  );

  const getDefaultValues = () => {
    const defaultValues = {
      name: step.name,
      htmlMessage: step.configs.$$editorHtmlMessage,
      message: step.configs.message,
      type: step.configs.type,
      ephemeral: !!step.configs.ephemeral,
      removeUrlEmbeds: step.configs.removeUrlEmbeds,
      channelId: step.configs.channelId,
      requiredQuestion: !skipOption,
      keywordToSkip: skipOption ? skipOption.label : DEFAULT_SKIP_KEYWORD,
      stepType: step.type,
      delayMinutes:
        typeof step.configs.delaySeconds === "number"
          ? step.configs.delaySeconds / 60
          : 0,
      delayed:
        typeof step.configs.delaySeconds === "number" &&
        step.configs.delaySeconds > 0,
    };
    const { type: stepType } = step;

    if (stepType === "send_survey") {
      return {
        ...defaultValues,
        options:
          step.configs.type === "buttons"
            ? step.configs.options
                .filter((o) => o.id !== skipId)
                .map((o) => ({ ...o, key: o.id }))
            : step.configs.options
                .filter((o) => o !== DEFAULT_SKIP_KEYWORD)
                .map((o) => ({ emoji: null, label: o })),
        multipleSelection: false,
      };
    } else if (stepType === "send_multiple_select") {
      //TODO set multiple select values
      return {
        ...defaultValues,
        options: step.configs.options
          .filter((o) => o.id !== skipId)
          .map((o) => ({ ...o, key: o.id })),
        multipleSelection: true,
      };
    }
    throw new Error(
      `Invalid step type ${stepType}, expected either send_survey or send_multiple_select`
    );
  };

  const defaultValues = getDefaultValues();

  const form = useForm({
    defaultValues,
    mode: "onChange",
  });
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
    setError,
    clearErrors,
    trigger,
  } = form;
  const {
    state,
    dispatch,
    onPersist: onPersist,
  } = useTriggerEditorStateDispatch();

  // This validation guarantees that all options have an emoji or a label. This is important because if a survey has an empty option the trigger flow will break.
  const validateOptions = (optionId: `options.${number}`) => {
    const optionStatus = watch(optionId);

    if (!Boolean(optionStatus.label) && !Boolean(optionStatus?.emoji)) {
      setError(`${optionId}.label`, {
        type: "custom",
        message:
          "Options cannot be empty, emoji or label field must be filled.",
      });
    } else {
      // trigger field validation programmatically
      trigger([`${optionId}.label`]);
    }
  };

  const isPersisting = state.editor.isPersisting === "save-draft";

  const { fields, append, remove } = useFieldArray({
    control,
    name: "options", // unique name for your Field Array
    //I did this because otherwise react-form will add custom keys
    ...(step.configs.type === "buttons" ? { keyName: "key" as "id" } : null),
  });
  watch(["requiredQuestion", "keywordToSkip", "multipleSelection"]);

  // Watching options property to update the required question property in send_survey component
  // We aren't using fieldArray from react-hook-form because we need update one option of all them
  // I'd say the "skip" option is a special option maybe I should create a new property within
  // the form instead of watching it but this works for now.
  useEffect(() => {
    const subscription = watch((value, info) => {
      // There is an issue with useForm tries to assign the label to the options
      // to avoid this we have to create a deep copy see https://github.com/react-hook-form/react-hook-form/discussions/3715
      const parseFormValues = JSON.parse(JSON.stringify(value));

      if (info.name === "requiredQuestion") {
        if (value.requiredQuestion) {
          removeSkipOption();
        } else {
          addSkipOption(parseFormValues);
        }
      }

      if (info.name === "keywordToSkip") {
        updateSkipOption(parseFormValues);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, step]);

  const readOnly = !state.trigger.current.isDraft;
  const onSubmit = (data: any) => {
    // This uses the immer library to create a new copy of step (with updates)
    // without affecting the original step.
    const newStep = produce(step, (stepDraft) => {
      stepDraft.name = data.name;
      stepDraft.configs.type = data.type;
      stepDraft.configs.channelId = data.channelId;
      stepDraft.configs.ephemeral = data.ephemeral;
      stepDraft.configs.removeUrlEmbeds = data.removeUrlEmbeds;
      stepDraft.configs.delaySeconds = data.delayMinutes
        ? data.delayMinutes * 60
        : 0;

      const isButtons = stepDraft.configs.type === "buttons";
      data.options.forEach((o: any, idx: number) => {
        let optionIdx = isButtons
          ? stepDraft.configs.options.findIndex(
              (so) => o.id === (so as ButtonOption).id
            )
          : idx;
        if (optionIdx < 0) {
          stepDraft.configs.options.push(data.options[idx]);
          optionIdx = idx;
        }
        if (stepDraft.configs.type === "buttons") {
          invariant(stepDraft.configs.type === "buttons");
          stepDraft.configs.options[optionIdx].label = o.label;
          stepDraft.configs.options[optionIdx].emoji = o.emoji;
          (stepDraft.configs.options[optionIdx] as ButtonOption).skipDefer =
            !!data.ephemeral;
        } else stepDraft.configs.options[optionIdx] = o.label;
      });

      let optionsIds: string[] = [];
      if (!data.requiredQuestion) {
        if (stepDraft.configs.type === "buttons") {
          stepDraft.configs.options = [
            ...data.options,
            {
              id: skipId,
              label: data.keywordToSkip,
              skipDefer: !!data.ephemeral,
            },
          ];
        } else {
          stepDraft.configs.options = [...data.options, DEFAULT_SKIP_KEYWORD];
        }
        optionsIds = [skipId];
      }

      //get all the options ids
      optionsIds = [
        ...optionsIds,
        ...data.options.map((o: any) => {
          return isButtons ? (o as ButtonOption).id : (o as string);
        }),
      ];

      //In case options were removed from the form we need remove it from the step
      stepDraft.configs.options = (stepDraft.configs.options as any[]).filter(
        (option) => {
          return isButtons
            ? optionsIds.includes((option as ButtonOption).id)
            : optionsIds.includes(option);
        }
      );

      stepDraft.configs.message = data.message;
      stepDraft.configs.$$editorHtmlMessage = data.htmlMessage;
      stepDraft.type = data.stepType;
    });

    dispatch(updateStep(newStep));
    dispatch(saveDraft(onPersist));
  };

  /**
   * Given a trigger step, find the index of the "skip option" out of the array of survey config
   * options
   * @param step
   * @returns
   */
  const getSkipOptionIndex = (step: TriggerStepSendSurvey) => {
    const isButtons = step.configs.type === "buttons";
    const surveyConfig = step.configs as StepConfigSendSurvey;
    return isButtons
      ? surveyConfig.options.findIndex((o) => skipId === (o as ButtonOption).id)
      : surveyConfig.options.findIndex((o) => skipId === o);
  };

  // If a skip option exists and the user hit required question checkbox in the form
  // we need to add the skip option
  const addSkipOption = (data: any) => {
    const stepUpdated = produce(step, (stepDraft) => {
      if (stepDraft.configs.type === "buttons") {
        stepDraft.configs.options = [
          ...data.options,
          {
            id: skipId,
            label: data.keywordToSkip,
          },
        ];
      } else {
        stepDraft.configs.options = [...data.options, DEFAULT_SKIP_KEYWORD];
      }
      return stepDraft;
    });

    dispatch(updateStep(stepUpdated));
    dispatch(saveDraft(onPersist));
  };

  const removeSkipOption = () => {
    const stepUpdated = produce(step, (stepDraft) => {
      const skipButtonIndex = getSkipOptionIndex(
        stepDraft as TriggerStepSendSurvey
      );

      // If a skip option exists and the user hit required question checkbox in the form
      // we need to remove the skip option
      if (skipButtonIndex !== -1) {
        stepDraft.configs.options.splice(skipButtonIndex, 1);
        const skipTransitionIndex = stepDraft.transitions.findIndex(
          (t) =>
            isWhenUserResponse(t.when) &&
            t.when.userResponse.type === "button" &&
            t.when.userResponse.buttonId === "skip"
        );
        if (skipTransitionIndex !== -1) {
          stepDraft.transitions.splice(skipTransitionIndex, 1);
        }
      }
      return stepDraft;
    });

    dispatch(updateStep(stepUpdated));
    dispatch(saveDraft(onPersist));
  };

  const updateSkipOption = (data: any) => {
    const stepUpdated = produce(step, (stepDraft) => {
      const skipButtonIndex = getSkipOptionIndex(
        stepDraft as TriggerStepSendSurvey
      );

      if (skipButtonIndex !== -1) {
        if (stepDraft.configs.type === "buttons") {
          stepDraft.configs.options[skipButtonIndex].label = data.keywordToSkip;
        } else {
          stepDraft.configs.options[skipButtonIndex] = data.keywordToSkip;
        }
      }
    });
    dispatch(updateStep(stepUpdated));
    dispatch(saveDraft(onPersist));
  };

  return (
    <StepFormWithCommonParts
      form={form}
      onSubmit={handleSubmit(onSubmit)}
      step={step}
    >
      <SendStepFormWithCommonParts
        step={step}
        form={form}
        insertVariables={insertVariables}
      >
        <FormLabel label="Type of survey">
          <select {...register("type")} style={{ width: "100%" }}>
            {/*<option value={"emojis"}>Emojis</option>*/}
            <option value={"buttons"}>Buttons</option>
          </select>
        </FormLabel>
        {/*Required question, it creates a new branch*/}
        <FormLabel label="">
          <div>
            <Controller
              control={control}
              name="requiredQuestion"
              render={({ field: { onChange } }) => (
                <>
                  <Checkbox
                    checked={!skipOption}
                    onChange={(e) => onChange(e.target.checked)}
                    disabled={
                      !skipOption && fields.length >= MAX_SURVEY_OPTIONS
                    }
                  >
                    <span className="text-gray-500">Required question</span>
                  </Checkbox>
                </>
              )}
            />
          </div>
        </FormLabel>
        {skipOption && (
          <FormLabel label="Keyword to skip">
            <div style={{ width: "100%" }}>
              <Input style={{ width: "100%" }} {...register("keywordToSkip")} />
            </div>
          </FormLabel>
        )}

        {/* Options */}
        <FormLabel label="">
          {fields.map((f, idx) => (
            <div key={f.id} className="w-full">
              <div
                className="flex justify-between relative mb-1"
                onClick={(event) => {
                  // We have to prevent event bubbling
                  event.preventDefault();
                }}
              >
                <div className="font-bold">Option {idx + 1}</div>

                <div
                  className={clsx("flex items-center", {
                    "pointer-events-none opacity-50": isPersisting,
                    "cursor-pointer	": !isPersisting,
                  })}
                >
                  <CloseCircleFilled
                    onClick={() => {
                      remove(idx);
                      onSubmit(form.getValues());
                    }}
                  />
                </div>
              </div>

              <div
                className="flex w-full flex-row gap-3"
                onClick={(event) => event.preventDefault()}
              >
                <div className="basis-1/5">
                  {" "}
                  <EmojiPicker
                    emoji={f.emoji}
                    onEmojiClick={(emoji: string) => {
                      setValue(`options.${idx}.emoji`, emoji);
                      validateOptions(`options.${idx}`);
                    }}
                  />
                </div>
                <Input
                  className="basis-4/5"
                  key={f.id}
                  style={{ flexGrow: 1 }}
                  readOnly={readOnly || isPersisting}
                  {...register(`options.${idx}.label`, {
                    maxLength: 80,
                  })}
                  onChange={(e) => {
                    const optValue = e.target.value;
                    setValue(`options.${idx}.label`, optValue);
                    validateOptions(`options.${idx}`);
                  }}
                />
              </div>
              {errors?.options && errors?.options[idx] && (
                <p className="text-red-500 m-0">
                  Error:{" "}
                  {errors?.options[idx]?.label?.message
                    ? errors?.options[idx]?.label?.message
                    : "Options cannot exceed 80 characters"}
                </p>
              )}
            </div>
          ))}
        </FormLabel>
        {!readOnly && (
          <button
            // If skip option is active it means that we have an option more. So we need to add one more
            // because fields property are the options without the skip option
            disabled={
              (skipOption ? 1 : 0) + fields.length >= MAX_SURVEY_OPTIONS
            }
            className={
              (skipOption ? 1 : 0) + fields.length >= MAX_SURVEY_OPTIONS
                ? "opacity-50 cursor-not-allowed"
                : "border border-dashed rounded-md py-1 mb-3"
            }
            onClick={(event) => {
              event.preventDefault();
              append(
                {
                  id: `${nanoid(6)}-option`,
                  label: `option ${fields.length + 1}`,
                },
                { shouldFocus: true }
              );
            }}
          >
            <PlusOutlined className="mr-1" /> Add Option
          </button>
        )}
      </SendStepFormWithCommonParts>
    </StepFormWithCommonParts>
  );
}
